import { create } from "zustand";

export type SubscriptionLevel = "basic" | "standard" | "premium" | "enterprise";

interface FeatureFlagsState {
  subscriptionLevel: SubscriptionLevel;
  isDemoMode: boolean;
  setSubscriptionLevel: (level: SubscriptionLevel) => void;
  setDemoMode: (isDemo: boolean) => void;
  hasAccessToFeature: (feature: string) => boolean;
}

const FEATURE_ACCESS: Record<SubscriptionLevel, string[]> = {
  basic: ["basic-dashboard", "lighting-control", "floorplan-navigation"],
  standard: [
    "basic-dashboard",
    "lighting-control",
    "floorplan-navigation",
    "energy-analytics",
    "real-time-monitoring",
    "custom-scenes",
    "occupancy-control",
    "maintenance-alerts",
  ],
  premium: [
    "basic-dashboard",
    "lighting-control",
    "floorplan-navigation",
    "energy-analytics",
    "real-time-monitoring",
    "custom-scenes",
    "occupancy-control",
    "maintenance-alerts",
    "bacnet-integration",
    "advanced-scheduling",
    "admin-access",
    "report-export",
    "multi-site-management",
    "daylight-harvesting",
    "load-shedding",
    "demand-response",
    "api-access",
    "mobile-app",
    "cloud-storage",
  ],
  enterprise: [
    "basic-dashboard",
    "lighting-control",
    "floorplan-navigation",
    "energy-analytics",
    "real-time-monitoring",
    "custom-scenes",
    "occupancy-control",
    "maintenance-alerts",
    "bacnet-integration",
    "advanced-scheduling",
    "admin-access",
    "report-export",
    "multi-site-management",
    "daylight-harvesting",
    "load-shedding",
    "demand-response",
    "api-access",
    "mobile-app",
    "cloud-storage",
    "advanced-reporting",
    "custom-integrations",
    "priority-support",
    "training-access",
    "firmware-updates",
    "remote-access",
    "extended-data-retention",
    "custom-branding",
    "multi-user-access",
    "role-based-access",
    "audit-logs",
    "compliance-reporting",
    "predictive-analytics",
    "energy-benchmarking",
    "utility-bill-integration",
    "cost-allocation",
    "carbon-reporting",
    "sustainability-metrics",
  ],
};

export const useFeatureFlagsStore = create<FeatureFlagsState>((set, get) => ({
  subscriptionLevel: "basic",
  isDemoMode: true,
  setSubscriptionLevel: (level: SubscriptionLevel) =>
    set({ subscriptionLevel: level }),
  setDemoMode: (isDemo: boolean) => set({ isDemoMode: isDemo }),
  hasAccessToFeature: (feature: string) => {
    const { subscriptionLevel, isDemoMode } = get();
    if (isDemoMode) return true;
    return FEATURE_ACCESS[subscriptionLevel].includes(feature);
  },
}));

// Create a singleton instance for non-React usage
export const featureFlagService = {
  getState: () => useFeatureFlagsStore.getState(),
  setState: (state: Partial<FeatureFlagsState>) =>
    useFeatureFlagsStore.setState(state),
  subscribe: (listener: (state: FeatureFlagsState) => void) =>
    useFeatureFlagsStore.subscribe(listener),
};
